import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { Box, Typography } from '@mui/material';
import {
  formatNumber,
  NumberFormatPreset,
  QuickViewReferenceValue,
  QuickViewWidget,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { ContractVariant, I18nNamespace } from '~/common/types';
import { CubeChart, CubeChartType } from '~/components/CubeChart';
import { monthsInYearFormatter } from '~/components/CubeChart/CubeChart.common';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { getSegments, lastYearDateRange } from '~/util/cube';

const formatValueCompact = formatNumber(NumberFormatPreset.CURRENCY_COMPACT);
const formatValue = formatNumber(NumberFormatPreset.CURRENCY);
const formatQuantityCompact = formatNumber(NumberFormatPreset.INTEGER_COMPACT);
const formatQuantity = formatNumber(NumberFormatPreset.INTEGER);

interface ContractsTotalWidgetProps {
  variant: ContractVariant;
}

export const ContractsTotalWidget = ({ variant }: ContractsTotalWidgetProps) => {
  const key = `ContractsTotal${variant.charAt(0) + variant.slice(1).toLowerCase()}Widget`;
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, {
    keyPrefix: key,
  });
  const [widgetRef, active] = useScrollIntoView({
    hash: key,
  });

  const filters: Filter[] = [
    {
      member: 'WidgetContracts.supplierId',
      operator: 'equals',
      values: [entityId],
    },
    {
      member: 'WidgetContracts.contractVariant',
      operator: 'equals',
      values: [variant],
    },
  ];

  const queryValue = useCubeQuery({
    measures: ['ContractUtilization.totalSpent', 'ContractUtilization.totalPlanned'],
    timeDimensions: [
      {
        dimension: 'WidgetContracts.currentDate',
        granularity: 'month',
        dateRange: lastYearDateRange,
      },
    ],
    filters,
    segments: ['WidgetContracts.onlyValidContracts'],
  });

  const { segments, totalValue: yearlyTotalSpent } = getSegments(queryValue);
  const { totalValue: yearlyTotalPlanned } = getSegments(queryValue, 1);
  const value = variant === ContractVariant.VALUE ? formatValue(yearlyTotalSpent) : formatQuantity(yearlyTotalSpent);
  const compactValue =
    variant === ContractVariant.VALUE ? formatValueCompact(yearlyTotalSpent) : formatQuantityCompact(yearlyTotalSpent);
  const referenceValue =
    variant === ContractVariant.VALUE
      ? formatValueCompact(yearlyTotalPlanned)
      : formatQuantityCompact(yearlyTotalPlanned);

  return (
    <QuickViewWidget
      ref={widgetRef}
      active={active}
      loading={queryValue.isLoading}
      title={t('title')}
      subTitle={t('subTitle')}
      value={compactValue}
      valueTooltip={value}
      data-intercom-target={key}
      referenceValue={<QuickViewReferenceValue description={t('description')} value={referenceValue} />}
    >
      {segments && yearlyTotalSpent ? (
        <Box
          sx={{
            display: 'flex',
            flex: '1 1 0',
            alignItems: 'flex-end',
          }}
        >
          <Box
            sx={{
              flex: '1 1 0',
              display: 'flex',
              mb: '-5px',
              mr: 3,
            }}
          >
            <Typography component="div" variant="caption" color="textPrimary">
              {t('chartCaption')}
            </Typography>
          </Box>
          <CubeChart
            type={CubeChartType.tinyBar}
            formatter={{
              x: monthsInYearFormatter,
              y: formatValueCompact,
            }}
            {...queryValue}
          />
        </Box>
      ) : (
        <PlaceholderWidget />
      )}
    </QuickViewWidget>
  );
};
